import React from 'react'
import { Card as AlliumCard } from '@telus-uds/components-web'

import { mapDataAttrsToDataSet } from '../../../utils/dataAttributes'
import { ResponsiveProps } from '../../../utils/types/components'
import Image, { type BasicImageProps } from '../Image/Image'

export interface FileProps {
  fileName: string
  url: string
}

type FullBleedImagePositions = 'none' | 'top' | 'bottom' | 'left' | 'right'
type FullBleedContentAlignmentOptions = 'start' | 'center' | 'end'

type BackgroundImageResizeOptions = 'center' | 'contain' | 'cover' | 'repeat' | 'stretch'
type CardStyleOptions =
  | 'default'
  | 'grid'
  | 'light'
  | 'lighter'
  | 'lightest'
  | 'dark'
  | 'darker'
  | 'darkest'
  | 'feature'
  | 'feature2'
  | 'feature3'
  | 'feature4'

export type CardProps = {
  children: React.ReactNode
  background?: 'alternative' | 'subtle' | 'grid' | 'feature' | 'plain'
  padding?: 'narrow' | 'intermediate' | 'compact' | 'custom'
  borderRadius?: 'default' | 'none' | 'small' | 'large'
  fullBleedImagePosition?: ResponsiveProps<FullBleedImagePositions>
  fullBleedContentAlignment?: ResponsiveProps<FullBleedContentAlignmentOptions>
  fullBleedTextAlignment?: ResponsiveProps<FullBleedContentAlignmentOptions>
  fullBleedImage?: BasicImageProps
  fullBleedImgCol?: ResponsiveProps<number>
  decorative?: boolean
  imgAltText?: string
  limitHeight?: boolean
  useInteractiveCard?: boolean
  interactiveCardUrl?: string
  imageStyle?: string
  backgroundImageResizeOption?: ResponsiveProps<BackgroundImageResizeOptions>
  style?: CardStyleOptions
  customBackgroundColor?: string
  customBorderColor?: string
}

const Card = ({
  children,
  background,
  padding,
  borderRadius = 'default',
  fullBleedImagePosition = undefined,
  fullBleedContentAlignment = undefined,
  fullBleedTextAlignment = undefined,
  fullBleedImage,
  fullBleedImgCol,
  decorative = false,
  imgAltText = '',
  limitHeight = true,
  useInteractiveCard = false,
  interactiveCardUrl,
  imageStyle = 'full_bleed',
  backgroundImageResizeOption = undefined,
  style,
  customBackgroundColor = undefined,
  customBorderColor = undefined,
  ...rest
}: CardProps) => {
  const CardWrapper = limitHeight ? 'div' : React.Fragment
  const handleInteractiveCardPress = () => (window.location.href = interactiveCardUrl)

  return (
    <CardWrapper data-testid="card-wrapper-testid">
      <AlliumCard
        testID="card-testid"
        fullBleedContent={
          imageStyle === 'full_bleed' && fullBleedImage?.src && !useInteractiveCard
            ? {
                position: fullBleedImagePosition,
                align: fullBleedContentAlignment,
                contentAlign: fullBleedTextAlignment,
                imgCol: fullBleedImgCol,
                content: (
                  <Image
                    src={fullBleedImage.src}
                    width={fullBleedImage.width}
                    height={fullBleedImage.height}
                    // if imgAltText empty use the text from the image description
                    alt={imgAltText || fullBleedImage.alt}
                    decorative={decorative}
                    // TODO: implement sizes
                    // sizes={{ sm: 100, md: 50, xl: 35 }}
                  />
                ),
              }
            : undefined
        }
        backgroundImage={
          imageStyle === 'background' && fullBleedImage?.src && !useInteractiveCard
            ? {
                alt: imgAltText || fullBleedImage.alt,
                resizeMode: backgroundImageResizeOption,
                src: fullBleedImage.src,
              }
            : undefined
        }
        variant={{
          // This allows existing cards that use background to continue to work, new cards will use style as background is now deprecated
          background: style === undefined ? background : undefined,
          style,
          padding,
          borderRadius,
        }}
        tokens={{
          backgroundColor: customBackgroundColor,
          borderColor: customBorderColor,
        }}
        dataSet={mapDataAttrsToDataSet(rest)}
        interactiveCard={
          useInteractiveCard
            ? {
                body: children,
                variant: { padding, borderRadius },
                href: interactiveCardUrl,
              }
            : undefined
        }
        onPress={useInteractiveCard && interactiveCardUrl ? handleInteractiveCardPress : undefined}
      >
        {!useInteractiveCard && children}
      </AlliumCard>
    </CardWrapper>
  )
}

export default Card
